import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { SiteLayout } from "../components/site/SiteLayout";
import { useSeo } from "../lib/seo";
import { Reveal } from "../lib/reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InsightsListPage() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("All");
  useEffect(() => {
    axios.get(`${API}/insights?limit=100`).then((r) => setPosts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  useSeo({
    title: "Insights — AgriTech, Farm Management Software & Agricultural Automation",
    description: "Insights from Wald on agricultural software development, AgriTech, digital transformation, agricultural automation, traceability and agricultural data analytics for South African agri exporters.",
    path: "/insights",
  });
  const categories = ["All", ...Array.from(new Set(posts.map((p) => p.category)))];
  const filtered = filter === "All" ? posts : posts.filter((p) => p.category === filter);
  return (
    <SiteLayout>
      <section className="border-b border-border">
        <div className="container-wald py-20 md:py-28">
          <div className="eyebrow mb-6">Insights</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-4xl">
            Field-tested thinking on agri, software and operations.
          </h1>
          <p className="mt-6 max-w-2xl opacity-75 leading-relaxed">
            No marketing fluff. Everything published here comes out of engagements we have shipped.
          </p>
        </div>
      </section>
      <section className="border-b border-border sticky top-16 z-30 bg-background">
        <div className="container-wald py-4 flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`insights-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(cat)}
              className={`text-xs px-3 py-1.5 border transition-colors ${
                filter === cat
                  ? "border-[color:var(--wald-copper)] text-[color:var(--wald-copper)]"
                  : "border-border opacity-70 hover:opacity-100 hover:border-[color:var(--wald-copper)]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>
      <section>
        <div className="container-wald py-16">
          {filtered.length === 0 ? (
            <div className="opacity-60 text-sm py-16">No insights available in this category yet.</div>
          ) : (
            <div className="border-t border-l border-border grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((p) => (
                <Reveal key={p.id} className="border-b border-r border-border">
                  <Link
                    to={`/insights/${p.slug}`}
                    data-testid={`insight-card-${p.slug}`}
                    className="block p-8 h-full group hover:bg-card transition-colors"
                  >
                    <div className="eyebrow mb-4">{p.category}</div>
                    <h2 className="font-display text-xl md:text-2xl tracking-tight leading-snug mb-4">
                      {p.title}
                    </h2>
                    <p className="text-sm opacity-70 leading-relaxed mb-6">{p.excerpt}</p>
                    <div className="flex items-center justify-between text-xs opacity-60">
                      <span>{p.author}</span>
                      <span className="font-mono-tab">
                        {(p.published_at || p.created_at || "").slice(0, 10)}
                      </span>
                    </div>
                    <div className="mt-6 inline-flex items-center gap-1.5 copper-link text-sm">
                      Read <ArrowRight size={14} strokeWidth={2} />
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
